interface ChatHeaderProps {
    nickname: string;
    connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error' | 'reconnecting';
    onLeave: () => void;
    isLeaving: boolean;
    canLeave: boolean;
}

/**
 * Chat room header with connection status and leave functionality
 * @param {ChatHeaderProps} props - Component props
 * @param {string} props.nickname - Current user's nickname
 * @param {string} props.connectionStatus - WebSocket connection status
 * @param {Function} props.onLeave - Function to leave chat room
 * @param {boolean} props.isLeaving - Whether leave operation is in progress
 * @param {boolean} props.canLeave - Whether user can leave the room
 * @returns {JSX.Element} Fixed header with room info and controls
 */
export const ChatHeader = ({ nickname, connectionStatus, onLeave, isLeaving, canLeave }: ChatHeaderProps) => {
    /**
     * Get status indicator color based on connection state
     * @returns {string} Tailwind CSS background color class
     */
    const getStatusColor = () => {
        switch (connectionStatus) {
            case 'connected':
                return 'bg-green-500';
            case 'connecting':
            case 'reconnecting':
                return 'bg-yellow-500';
            default:
                return 'bg-red-500';
        }
    };

    /**
     * Handle back arrow click to leave room
     */
    const handleArrowClick = () => {
        if (canLeave) {
            onLeave();
        }
    };

    return (
        <header className="h-[52px] fixed top-0 z-50 w-full bg-white flex items-center justify-between gap-2 py-3 px-4">
            <button onClick={handleArrowClick} disabled={isLeaving}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="m15 18-6-6 6-6" />
                </svg>
            </button>

            <div className="flex-1 min-w-0 flex flex-col items-center">
                <span className="truncate text-base font-medium">Chat Room</span>
                <span className="text-xs text-[#53555B]">{nickname}</span>
            </div>

            <div className="flex items-center gap-1">
                <div className={`w-2 h-2 rounded-full ${getStatusColor()}`} />
                <span className="text-xs text-[#53555B] capitalize">{connectionStatus}</span>
            </div>
        </header>
    );
};
